import { map, pipe, reduce } from 'lodash/fp';
import { values } from 'lodash';
import { ExerciseState } from '../entities/Exercise';
const toSafeDivision = (divider) => divider || 1;
const calculateSum = reduce(function (sum, n) {
    return sum + n;
}, 0);
export default class ProgressService {
    constructor(props) {
        // This is loaded from "props" so we don't need to initialize content both here,
        // and in the other content service.
        this.contentService = props.contentService;
    }
    /**
     * Adds user progress to exercises
     * section -> [blocks] -> [exercises]
     * progress calculates from right to left
     * @param userProgress
     */
    calculateUserProgress(userProgress) {
        const allContent = this.contentService.getContent();
        const allBlocks = this.contentService.getBlocks();
        const allSections = this.contentService.getSections();
        const content = Object.keys(allContent).reduce((acc, title) => {
            var _a;
            return (acc = Object.assign(Object.assign({}, acc), { [title]: Object.assign(Object.assign({}, Reflect.get(allContent, title)), { state: (_a = userProgress === null || userProgress === void 0 ? void 0 : userProgress[title]) === null || _a === void 0 ? void 0 : _a.state }) }));
        }, {});
        const blocks = Object.keys(allBlocks).reduce((acc, blockId) => {
            const block = Reflect.get(allBlocks, blockId);
            return Object.assign(Object.assign({}, acc), { [blockId]: Object.assign(Object.assign({}, block), { progress: this.getBlockProgress(block.content, content) }) });
        }, {});
        const sections = Object.keys(allSections).reduce((acc, sectionId) => {
            const section = Reflect.get(allSections, sectionId);
            return Object.assign(Object.assign({}, acc), { [sectionId]: Object.assign(Object.assign({}, section), { progress: this.getSectionProgress(section.blocks, blocks) }) });
        }, {});
        return { content, blocks, sections };
    }
    calculateABCProgress(contentStats = {}) {
        const content = this.contentService.getABCContent();
        const sections = content.sections;
        const progress = {};
        Object.values(sections).forEach(({ id, exercises }) => {
            const totalProgress = 2 * exercises.length;
            const letters = {};
            let userProgress = 0;
            exercises.forEach(exercise => {
                const exerciseProgress = contentStats[exercise];
                const _letters = values(exerciseProgress === null || exerciseProgress === void 0 ? void 0 : exerciseProgress.letters);
                const completedExercises = _letters.length;
                letters[exercise] = _letters;
                userProgress += completedExercises;
            });
            progress[id] = {
                letters,
                progress: userProgress / totalProgress,
            };
        });
        return progress;
    }
    calculateABCProgressBookWorm(contentStats = {}) {
        const content = this.contentService.getABCContent();
        const sections = content.sections;
        const progress = {};
        Object.values(sections).forEach(({ id, exercises, letters }) => {
            const totalProgress = exercises.length;
            let userProgress = 0;
            const currentSectionLetters = letters.split('');
            exercises.forEach(exercise => {
                const currentExercise = contentStats[exercise];
                const completedExercises = (currentExercise === null || currentExercise === void 0 ? void 0 : currentExercise.state) >= 3 || (currentExercise === null || currentExercise === void 0 ? void 0 : currentExercise.solvedOnce) ? 1 : 0;
                userProgress += completedExercises;
            });
            const numberOfStepToReceiveLetter = 100 / currentSectionLetters.length;
            const numberOfOpenLetters = Math.floor(((userProgress / totalProgress) * 100) / numberOfStepToReceiveLetter);
            progress[id] = {
                letters: Array.from({ length: numberOfOpenLetters }, (_, index) => currentSectionLetters[index]),
                progress: userProgress / totalProgress,
            };
        });
        return progress;
    }
    isExerciseSolved(state) {
        return state === ExerciseState.Solved || state === ExerciseState.UsedAlbertHelp;
    }
    calculateSubChapterProgress(skills, userProgress) {
        return skills === null || skills === void 0 ? void 0 : skills.map((skill) => {
            var _a;
            return ({
                name: skill.name,
                progress: (_a = skill.exercises) === null || _a === void 0 ? void 0 : _a.reduce((total, exerciseId) => {
                    var _a, _b;
                    const isSolved = this.isExerciseSolved((_a = userProgress === null || userProgress === void 0 ? void 0 : userProgress[exerciseId]) === null || _a === void 0 ? void 0 : _a.state);
                    const progress = 1 / toSafeDivision((_b = skill.exercises) === null || _b === void 0 ? void 0 : _b.length);
                    return isSolved ? total + progress : total;
                }, 0),
            });
        });
    }
    calculateSeniorUserProgress(userProgress, chapters) {
        return pipe(map((chapter) => (Object.assign(Object.assign({}, chapter), { skills: this.calculateSubChapterProgress(chapter.skills, userProgress) }))), map(chapter => {
            var _a, _b;
            return (Object.assign(Object.assign({}, chapter), { progress: ((_a = chapter.skills) === null || _a === void 0 ? void 0 : _a.reduce((total, skill) => total + skill.progress, 0)) /
                    toSafeDivision((_b = chapter.skills) === null || _b === void 0 ? void 0 : _b.length) }));
        }))(chapters);
    }
    /**
     * @description Returns the star score for a junior piece of content
     * based on the success rate.
     * @param successRate
     * @param limitLow - optionally change the range between 1 and 2 stars
     * @param limitHigh - optionally change the range between 2 and 3 stars
     */
    getStars(successRate, limitLow = 34, limitHigh = 67) {
        if (successRate < limitLow) {
            return 1;
        }
        if (successRate >= limitHigh) {
            return 3;
        }
        return 2;
    }
    getStarsForExercise(successRate) {
        return this.getStars(successRate, 34, 67);
    }
    getStarsForBossFight(successRate) {
        return this.getStars(successRate, 65, 75);
    }
    getBlockProgress(items, content) {
        if (!(items === null || items === void 0 ? void 0 : items.length)) {
            return;
        }
        const correctAnswer = items.filter((it) => { var _a, _b; return (_b = (_a = content === null || content === void 0 ? void 0 : content[it]) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : 0 >= 2; });
        return {
            progress: correctAnswer.length / toSafeDivision(items.length),
            correctAnswer: correctAnswer.length,
            exercise: items.length,
        };
    }
    getSectionProgress(items, blocks) {
        const correctAnswer = pipe(map((it) => { var _a, _b; return (_b = (_a = blocks === null || blocks === void 0 ? void 0 : blocks[it]) === null || _a === void 0 ? void 0 : _a.progress) === null || _b === void 0 ? void 0 : _b.correctAnswer; }), calculateSum)(items);
        const exercise = pipe(map((it) => { var _a, _b; return (_b = (_a = blocks === null || blocks === void 0 ? void 0 : blocks[it]) === null || _a === void 0 ? void 0 : _a.progress) === null || _b === void 0 ? void 0 : _b.exercise; }), calculateSum)(items);
        return correctAnswer / toSafeDivision(exercise);
    }
}
//# sourceMappingURL=progressService.js.map